import { enums } from "@shared/constants";
import { DataProvider } from "@shared/constants/common";
import {
  CollectEnhancedInfoType,
  GuestQuestionType,
} from "@shared/modules/basket/types/common/serializer-types";
import { useStore } from "@shared/store/useStore";
import { useTranslation } from "@shared/utils/internationalization";
import { useCallback, useMemo } from "react";

export function useEnhancedInfo(
  basketType: DataProvider,
  requireInfoCollection: boolean
) {
  const { t } = useTranslation("constants");

  // should show the field only if it's not already collected by a multiguest form
  const shouldCollectField = useCallback(
    (field: keyof CollectEnhancedInfoType) => {
      const basket = useStore.getState().getBasket(basketType);

      return Boolean(
        basket?.[field] &&
          !basket?.item_groups?.find(
            (ig) => ig.collect_info_fields?.[field] && ig.info_required // info_required is true when we need to collect for each item group
          )
      );
    },
    [basketType]
  );

  const collectCompany = shouldCollectField("collect_info_company");
  const collectJobTitle = shouldCollectField("collect_info_job_title");
  const collectStudentNumber = shouldCollectField(
    "collect_info_student_number"
  );
  const collectBirthday = shouldCollectField("collect_info_birthday");
  const collectLicensePlate = shouldCollectField("collect_info_license_plate");
  const collectHomeAddress = shouldCollectField("collect_info_home_address");

  // returns if address info needs to be collected
  // default value is collectHomeAddress (see above)
  const hasAddressInfo = useCallback(
    (hasInfo: boolean | undefined = collectHomeAddress) => {
      return hasInfo;
    },
    [collectHomeAddress]
  );

  // for each field, return true if the collection field on the basket is true
  // AND the collection field is false on all of the itemgroups
  // this prevents fields from being displayed multiple times (on multiguest groups and on the enhanced form)
  const defaultEnhancedInfo = useMemo(() => {
    return {
      collect_info_company: collectCompany,
      collect_info_job_title: collectJobTitle,
      collect_info_student_number: collectStudentNumber,
      collect_info_birthday: collectBirthday,
      collect_info_license_plate: collectLicensePlate,
    };
  }, [
    collectCompany,
    collectJobTitle,
    collectStudentNumber,
    collectBirthday,
    collectLicensePlate,
  ]);

  // returns true if enhanced non-address info needs to be collected
  // see defaultEnhancedInfo for default values
  const hasEnhancedInfo = useCallback(
    (infoObj: CollectEnhancedInfoType | undefined = defaultEnhancedInfo) => {
      return Boolean(
        infoObj.collect_info_company ||
          infoObj.collect_info_job_title ||
          infoObj.collect_info_student_number ||
          infoObj.collect_info_birthday ||
          infoObj.collect_info_license_plate
      );
    },
    [defaultEnhancedInfo]
  );

  // returns an array of formatted question objects
  // see defaultEnhancedInfo for default values
  const getEnhancedInfoQuestions = useCallback(
    (infoObj: CollectEnhancedInfoType | undefined = defaultEnhancedInfo) => {
      const enhancedInfoArray: Array<GuestQuestionType> = [];

      const defaultObj = {
        help_text: "",
        response: null,
        ticket_types: [],
        required: requireInfoCollection,
        type: enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.text.id,
        maxlength:
          enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.text.max_response_length,
      };

      if (infoObj.collect_info_company) {
        enhancedInfoArray.push({
          ...defaultObj,
          id: "info_company",
          title: t("Company"),
        });
      }
      if (infoObj.collect_info_job_title) {
        enhancedInfoArray.push({
          ...defaultObj,
          id: "info_job_title",
          title: t("Job Title"),
        });
      }
      if (infoObj.collect_info_student_number) {
        enhancedInfoArray.push({
          ...defaultObj,
          id: "info_student_number",
          title: t("Student Number"),
        });
      }
      if (infoObj.collect_info_birthday) {
        enhancedInfoArray.push({
          ...defaultObj,
          id: "info_birthday",
          title: t("Birthday"),
          type: enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.date.id,
          maxlength: undefined,
        });
      }
      if (infoObj.collect_info_license_plate) {
        enhancedInfoArray.push({
          ...defaultObj,
          id: "info_license_plate",
          title: t("Vehicle Registration Plate"),
          maxlength: 10, // value taken from Angular form
        });
      }
      return enhancedInfoArray;
    },
    [defaultEnhancedInfo]
  );

  return {
    hasAddressInfo,
    hasEnhancedInfo,
    getEnhancedInfoQuestions,
  };
}
